/**
 * EditMemberDialog - edit an existing member's name and roles.
 *
 * Name fields flow through generated `useForm`; the roles selector is a
 * composite `type: 'custom'` field (`MemberRolesField`) - one dropdown by
 * default, with an "Add role" button to grant additional roles. The
 * organisation owner's roles are immutable, so the roles selector renders
 * read-only for the owner (their name is still editable).
 */

import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import api from '@/admin/lib/api'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { useForm, normaliseClaimField } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { buildFormFieldFromMeta } from '@/shared/lib/forms/generated-metadata'
import { toastApiError } from '@/shared/lib/api-error'
import { MemberRolesField } from '@/admin/pages/settings/member-roles-field'
import {
  useMemberProfile,
  type ClaimMovePreview,
} from '@/admin/hooks/use-membership-attributes'
import { invalidateClaimsQueries } from '@/admin/hooks/use-claims'
import {
  decorateGeoZoneOptions,
  fieldErrorsFromApiError,
} from '@/admin/pages/settings/member-attribute-helpers'
import { useClaimMovesConfirm } from '@/admin/pages/settings/use-claim-moves-confirm'

interface MemberRole {
  uuid: string
  name: string
  is_admin: boolean
  is_primary: boolean
}

interface EditableMember {
  uuid: string
  user_first_name: string
  user_last_name: string
  user_email: string
  roles: MemberRole[]
  is_owner: boolean
}

interface RoleOption {
  uuid: string
  name: string
  is_admin: boolean
}

interface EditMemberDialogProps {
  orgUuid: string
  member: EditableMember | null
  roleOptions: RoleOption[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}

const FORM_ID = 'edit-member-form'

export function EditMemberDialog({
  orgUuid,
  member,
  roleOptions,
  open,
  onOpenChange,
  onSaved,
}: EditMemberDialogProps) {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Width only - the base DialogContent already supplies the p-6 body
          padding, gap-4 spacing, rounding and height cap (matches the standard
          modals, e.g. RoleEditDialog). */}
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[15px] text-[var(--ink)]">
            {t('users.editMember', 'Edit user')}
          </DialogTitle>
          <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
            {t('users.editMemberDesc', 'Update their name, roles, and custom fields.')}
          </p>
        </DialogHeader>
        {member && (
          <EditMemberForm
            key={member.uuid}
            orgUuid={orgUuid}
            member={member}
            roleOptions={roleOptions}
            onSaved={onSaved}
            onCancel={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function EditMemberForm({
  orgUuid,
  member,
  roleOptions,
  onSaved,
  onCancel,
}: {
  orgUuid: string
  member: EditableMember
  roleOptions: RoleOption[]
  onSaved: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation()

  // The member's role-scoped custom attributes (e.g. an RS's Geozone /
  // Platinum Geozone), rendered inline as generated fields in this same form -
  // no separate modal. Scoped server-side to the member's roles, and saved
  // alongside name/roles on the single Save. Only editable definitions become
  // inputs.
  const { data: profile } = useMemberProfile(member.uuid)
  const queryClient = useQueryClient()
  const { confirmClaimMoves, claimMovesDialog } = useClaimMovesConfirm()

  const attributeFields = useMemo<FormField[]>(() => {
    if (!profile) return []
    return profile.attribute_values
      .filter((attr) => attr.editable)
      .map((attr) => ({
        ...buildFormFieldFromMeta(
          normaliseClaimField({
            code_name: attr.code_name,
            display_name: attr.display_name,
            field_type: attr.field_type,
            required: attr.required,
            description: attr.description,
            configuration_data: decorateGeoZoneOptions(attr.configuration_data),
          }),
          attr.value,
        ),
        className: 'col-span-2',
      }))
  }, [profile])

  const attributeCodeNames = useMemo(
    () =>
      (profile?.attribute_values ?? [])
        .filter((attr) => attr.editable)
        .map((attr) => attr.code_name),
    [profile],
  )

  const fields = useMemo<FormField[]>(() => {
    const base: FormField[] = [
      {
        type: 'text',
        name: 'first_name',
        testId: 'edit-member-firstname',
        label: t('users.firstName', 'First Name'),
        value: member.user_first_name ?? '',
        autoFocus: true,
        required: t('users.firstNameRequired', 'First name is required'),
      },
      {
        type: 'text',
        name: 'last_name',
        testId: 'edit-member-lastname',
        label: t('users.lastName', 'Last Name'),
        value: member.user_last_name ?? '',
      },
      {
        type: 'custom',
        name: 'roles',
        className: 'col-span-2',
        label: t('users.rolesLabel', 'Roles'),
        value: member.roles.map((role) => role.uuid),
        validationSchema: member.is_owner
          ? z.array(z.string())
          : z
              .array(z.string())
              .min(1, t('users.atLeastOneRole', 'Select at least one role.'))
              .refine(
                (values) => values.every(Boolean),
                t('users.everyRoleRequired', 'Every role must be selected.'),
              ),
        render: (formContext) => {
          const value = (formContext.values.roles ?? []) as string[]
          const error = formContext.errors.roles as string | undefined
          return (
            <div className="col-span-2 flex flex-col gap-1.5">
              <span className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-2)]">
                {t('users.rolesLabel', 'Roles')}
              </span>
              <MemberRolesField
                value={value}
                onChange={(next) => formContext.setFieldValue('roles', next)}
                options={roleOptions}
                readOnly={member.is_owner}
                addLabel={t('users.addRole', 'Add role')}
                placeholder={t('users.selectRole', 'Select a role...')}
              />
              {member.is_owner && (
                <span className="text-[11px] text-[var(--ink-3)]">
                  {t(
                    'users.ownerRolesLocked',
                    "The organisation owner's roles cannot be changed.",
                  )}
                </span>
              )}
              {error && (
                <span className="text-[11px] text-[var(--status-error)]">
                  {error}
                </span>
              )}
            </div>
          )
        },
      },
    ]
    if (attributeFields.length === 0) return base
    // A "Custom fields" section heading, then the attribute inputs inline.
    const header: FormField = {
      type: 'custom',
      name: '__custom_fields_header',
      className: 'col-span-2',
      label: '',
      value: null,
      render: () => (
        <div className="col-span-2 mt-1 border-t-[0.5px] border-t-[var(--rule)] pt-3">
          <span className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-2)]">
            {t('members.customFields', 'Custom fields')}
          </span>
        </div>
      ),
    }
    return [...base, header, ...attributeFields]
  }, [member, roleOptions, t, attributeFields])

  const { Form, FormFooter } = useForm(
    {
      name: FORM_ID,
      submit: 'footer',
      fields,
    },
    async (rawValues, formContext) => {
      const values = rawValues as Record<string, unknown>
      const payload: Record<string, unknown> = {
        first_name: String(values.first_name ?? '').trim(),
        last_name: String(values.last_name ?? '').trim(),
      }
      if (!member.is_owner) {
        payload.role_uuids = ((values.roles as string[]) ?? []).filter(Boolean)
      }
      // Custom-field values ride in the same PUT, so name, roles and fields
      // save together or not at all.
      const attrPayload: Record<string, unknown> = {}
      for (const code of attributeCodeNames) attrPayload[code] = values[code]
      if (Object.keys(attrPayload).length > 0) payload.attribute_values = attrPayload
      try {
        const url = `/organisations/${orgUuid}/members/${member.uuid}/`
        // Handing this member a zone can move its open claims to them
        // (WPS-160). The preview runs the whole save - roles included - and
        // rolls it back, then the operator decides.
        let reassignOpenClaims = false
        if (payload.attribute_values) {
          const preview = await api
            .put(url, payload, { params: { preview: 1 } })
            .then((r) => (r.data.result ?? r.data) as ClaimMovePreview)
          if (preview.count > 0) {
            const name =
              [payload.first_name, payload.last_name]
                .map((part) => String(part ?? ''))
                .join(' ')
                .trim() || member.user_email
            const choice = await confirmClaimMoves(preview, name)
            // Cancelled: nothing saved, the form stays open.
            if (choice === null) return
            reassignOpenClaims = choice
          }
        }
        const response = await api.put(url, payload, {
          params: reassignOpenClaims ? { reassign_open_claims: 1 } : undefined,
        })
        const moved = Number(response.data?.reassigned_claims ?? 0)
        queryClient.invalidateQueries({ queryKey: ['member', orgUuid, member.uuid] })
        queryClient.invalidateQueries({ queryKey: ['geo-assignments'] })
        if (moved > 0) invalidateClaimsQueries(queryClient)
        toast.success(
          moved > 0
            ? t(
                'users.memberUpdatedClaimsMoved',
                'User updated. {{count}} open claim(s) moved.',
                { count: moved },
              )
            : t('users.memberUpdated', 'User updated.'),
        )
        onSaved()
      } catch (err) {
        // A per-field conflict (e.g. the one-RS-per-zone guard, WPS-160 #3)
        // renders inline on the offending zone field so the operator sees which
        // zone clashes and with whom - not just a generic toast. Only fields
        // this form actually renders get an inline error; anything else falls
        // back to the toast.
        const fieldErrors = fieldErrorsFromApiError(err)
        const inlineCodes = attributeCodeNames.filter((code) => fieldErrors[code])
        if (inlineCodes.length > 0) {
          for (const code of inlineCodes) {
            formContext.setFieldError(code, fieldErrors[code])
          }
        } else {
          toastApiError(err, t('users.errorUpdateMember', 'Failed to update user.'))
        }
      }
    },
    [member, orgUuid, fields, attributeCodeNames, queryClient, confirmClaimMoves],
  )

  return (
    <>
      <Form className="grid grid-cols-2 gap-3" />
      <DialogFooter className="flex justify-end gap-3 border-t-[0.5px] border-t-[var(--rule)] pt-4">
        {FormFooter && (
          <FormFooter onCancel={onCancel} submitText={t('common.save', 'Save')} />
        )}
      </DialogFooter>
      {claimMovesDialog}
    </>
  )
}
